import { useCallback, useState } from 'react';
import { liberarReserva, listarReservas } from '../../api/depositos.js';
import type { ReservaDeContagem } from '../../api/depositos.js';
import { mensagemDoErro } from '../../api/erros.js';
import { intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { useConsulta } from '../../api/useConsulta.js';
import { usePode } from '../../autorizacao/usePode.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import type { TomDoAviso } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { Cartao } from '../../componentes/Cartao.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { rotuloDoLocal } from '../../componentes/deposito.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { IconeInventarios } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';

type Props = {
  /** O mesmo recorte de filial da lista de depósitos logo acima. */
  readonly codFil: number | null;
};

type Aviso = { readonly tom: TomDoAviso; readonly texto: string };

/**
 * Depósitos travados para contagem agora (exclusividade do inventário).
 *
 * Só uma pessoa conta o mesmo depósito por vez; o coletor que tenta o segundo
 * recebe "Outra pessoa já está contando este depósito". Esta lista responde a
 * pergunta que vem em seguida — quem, e desde quando — e dá ao supervisor a
 * saída quando o aparelho que reservou sumiu: liberar. Liberar não apaga
 * contagem nenhuma, só destrava o depósito.
 *
 * Mora na tela de Depósitos, e não na de Inventários, porque a trava é do
 * DEPÓSITO: a tela de Inventários lista contagens que já chegaram, e uma
 * reserva é justamente a contagem que ainda não chegou.
 */
export function ReservasDeContagem({ codFil }: Props) {
  const pode = usePode();
  const libera = pode('inventarios.liberarReserva');

  const [pagina, definirPagina] = useState(1);
  const [aLiberar, definirALiberar] = useState<ReservaDeContagem | null>(null);
  const [erroDoDialogo, definirErroDoDialogo] = useState<string | null>(null);
  const [agindo, definirAgindo] = useState(false);
  const [aviso, definirAviso] = useState<Aviso | null>(null);

  const carregar = useCallback(
    () => listarReservas({ pagina, ...seInformado('codFil', codFil) }),
    [pagina, codFil],
  );

  const consulta = useConsulta(carregar);
  const { recarregar } = consulta;

  function liberar(reserva: ReservaDeContagem) {
    definirAgindo(true);
    definirErroDoDialogo(null);

    liberarReserva(reserva.id)
      .then(() => {
        definirAgindo(false);
        definirALiberar(null);
        definirAviso({
          tom: 'ok',
          texto: `${rotuloDoLocal(reserva.codFil, reserva.depositoErp)} está livre para contagem.`,
        });
        recarregar();
      })
      .catch((falha: unknown) => {
        definirAgindo(false);
        definirErroDoDialogo(mensagemDoErro(falha));
      });
  }

  const colunas: readonly Coluna<ReservaDeContagem>[] = [
    {
      chave: 'local',
      rotulo: 'Filial · depósito',
      largura: '30%',
      celula: (reserva) => (
        <span className="depositos__codigo">
          {rotuloDoLocal(reserva.codFil, reserva.depositoErp)}
        </span>
      ),
    },
    {
      chave: 'operador',
      rotulo: 'Quem está contando',
      largura: '34%',
      celula: (reserva) => (
        <span>
          {reserva.operador.nome}{' '}
          <span className="depositos__discreto">{reserva.operador.matricula}</span>
        </span>
      ),
    },
    {
      chave: 'desde',
      rotulo: 'Desde',
      largura: '20%',
      celula: (reserva) => formatarDataHora(reserva.reservadoEm),
    },
    {
      chave: 'acoes',
      rotulo: '',
      alinhamento: 'direita',
      celula: (reserva) =>
        libera ? (
          <Botao
            altura="pequena"
            variante="destrutivo"
            aria-label={`Liberar ${rotuloDoLocal(reserva.codFil, reserva.depositoErp)}`}
            onClick={() => {
              definirErroDoDialogo(null);
              definirALiberar(reserva);
            }}
          >
            Liberar
          </Botao>
        ) : null,
    },
  ];

  return (
    <Cartao titulo="Depósitos em contagem agora" semRespiro>
      {aviso !== null && <AvisoDaTela tom={aviso.tom}>{aviso.texto}</AvisoDaTela>}

      <Resultado
        consulta={consulta}
        espera="Carregando as reservas de contagem…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeInventarios tamanho={22} />}
            titulo="Nenhum depósito reservado para contagem"
            detalhe="Quando um operador começa a contar um depósito, ele aparece aqui até a contagem ser confirmada."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={colunas}
            chaveDe={(reserva) => reserva.id}
            descricao="Depósitos reservados para contagem"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'reserva', 'reservas')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>

      {aLiberar !== null && (
        <DialogoConfirmacao
          titulo={`Liberar ${rotuloDoLocal(aLiberar.codFil, aLiberar.depositoErp)}?`}
          rotuloConfirmar="Liberar"
          agindo={agindo}
          aoConfirmar={() => {
            liberar(aLiberar);
          }}
          aoCancelar={() => {
            definirALiberar(null);
            definirErroDoDialogo(null);
          }}
        >
          <p>
            {aLiberar.operador.nome} ({aLiberar.operador.matricula}) deixa de ter a exclusividade, e
            outra pessoa pode começar a contar este depósito.
          </p>
          <p>
            Nada é apagado: se a contagem dele ainda chegar, ela entra no ciclo e, havendo outra,
            vira conflito para decisão humana.
          </p>
          {erroDoDialogo !== null && <p className="depositos__erro">{erroDoDialogo}</p>}
        </DialogoConfirmacao>
      )}
    </Cartao>
  );
}
